import type { ReactNode } from "react";
import { Navigate, Outlet, useLocation } from "react-router";
import type { Permission } from "@/api/client";
import { FullScreenLoader } from "@/components/FullScreenLoader";
import { Forbidden } from "@/pages/ErrorPages";
import { useAuth } from "./AuthProvider";

/** Pages for signed-out visitors (login, register). Signed-in users go to the app. */
export function AnonymousOnly() {
  const { status } = useAuth();
  if (status === "loading") return <FullScreenLoader />;
  if (status === "authenticated") return <Navigate to="/app" replace />;
  return <Outlet />;
}

/** Requires a session. Remembers where the visitor was headed (App Flow §12). */
export function RequireAuth() {
  const { status } = useAuth();
  const location = useLocation();
  if (status === "loading") return <FullScreenLoader />;
  if (status === "anonymous") {
    const next = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/login?next=${next}`} replace />;
  }
  return <Outlet />;
}

/** Requires a workspace with onboarding finished; otherwise resumes onboarding. */
export function RequireWorkspace() {
  const { me } = useAuth();
  const location = useLocation();
  if (!me?.workspace || me.workspace.onboardingStep !== "done") {
    // Keeps ?connected= / ?error= from a marketplace connected during onboarding.
    return <Navigate to={`/onboarding${location.search}`} replace />;
  }
  return <Outlet />;
}

/** Shows "Akses ditolak" (App Flow §10) unless the role holds any of the permissions. */
export function RequirePermission({ any, children }: { any: Permission[]; children: ReactNode }) {
  const { can } = useAuth();
  return can(...any) ? <>{children}</> : <Forbidden />;
}

/** Only redirects targets inside the app, so ?next= cannot send users off-site. */
export function safeNext(raw: string | null): string {
  if (!raw) return "/app";
  try {
    const decoded = decodeURIComponent(raw);
    return decoded.startsWith("/") && !decoded.startsWith("//") ? decoded : "/app";
  } catch {
    return "/app";
  }
}
